const ROAMR_API_URL = process.env.REACT_APP_ROAMR_API_URL;
const API_HOST_V1 = `${ROAMR_API_URL}/v1`;
const API_HOST_V2 = `${ROAMR_API_URL}/v2`;
const API_HOST_V3 = `${ROAMR_API_URL}/v3`;

export const getOperatorData = async (operatorId) => {
  const url = `${API_HOST_V1}/web-startup?` + new URLSearchParams({
    operator: operatorId,
  });
  return fetch(url, {
    headers: {
      'Content-Type': 'application/JSON',
    },
  })
  .then((response) => {
    if (response.ok) {
      return response.json().then(data => {
        //console.log('getOperatorData response:', data);
        return data;
      });
    } else {
      throw new Error('There was a problem with your request. Please try again later');
    }
  })
  .catch((error) => {
    console.error(error);
    throw error;
  });
};

export const requestCode = async (phone, countryCode) => {
  const url = `${API_HOST_V1}/requestCode`; 
  const body = {
    phone,
    country_code: countryCode,
  };
  
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/JSON',
    },
  })
  .then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error('Failed to send SMS code. Please try again.');
    }
  })
  .catch((error) => {
    console.error(error);
    throw error;
  });
};

export const createCheckoutSession = async (params) => {
  const url = `${API_HOST_V3}/reservation-web/create-checkout-session`;
  
  console.log('createCheckoutSession - URL:', url);
  console.log('createCheckoutSession - Params:', params);
  
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  .then(async (response) => {
    console.log('createCheckoutSession - Response status:', response.status);
    console.log('createCheckoutSession - Response ok:', response.ok);
    
    if (response.ok) {
      const data = await response.json();
      console.log('createCheckoutSession - Success data:', data);
      return data;
    } else {
      // Parse error response for better error handling
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        const errorText = await response.text();
        console.error('createCheckoutSession - Error response (text):', errorText);
        throw new Error(`Failed to create checkout session. Status: ${response.status}`);
      }
      
      console.error('createCheckoutSession - Error response:', errorData);
      
      // Handle specific error types
      if (errorData.title === 'Price Mismatch') {
        const error = new Error(`Price Mismatch: Frontend amount $${errorData.frontend_total} doesn't match calculated amount $${errorData.calculated_total}`);
        error.errorData = errorData;
        throw error;
      }
      
      // Throw error with the backend message
      throw new Error(errorData.message || errorData.title || 'Failed to create checkout session');
    }
  })
  .catch((error) => {
    console.error('createCheckoutSession - Catch error:', error);
    throw error;
  });
};

// ACOS: unpaid create for UNMONETIZED fleets (fleets.is_monetized === false). No Stripe, no
// pricing — creates a `reserved` row and returns a reservation_hash for the confirmation page.
// The backend hard-gates on is_monetized === false, so this must only be called for such fleets.
export const createReservation = async (params) => {
  const url = `${API_HOST_V3}/reservation-web/create-reservation`;

  console.log('createReservation - URL:', url);
  console.log('createReservation - Params:', params);

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: {
      'Content-Type': 'application/json',
    },
  })
  .then(async (response) => {
    console.log('createReservation - Response status:', response.status);
    if (response.ok) {
      const data = await response.json();
      console.log('createReservation - Success data:', data);
      return data;
    }
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      throw new Error(`Failed to create reservation. Status: ${response.status}`);
    }
    console.error('createReservation - Error response:', errorData);
    throw new Error(errorData.message || errorData.title || 'Failed to create reservation');
  })
  .catch((error) => {
    console.error('createReservation - Catch error:', error);
    throw error;
  });
};

// ACOS: shared helper — create a free (unmonetized) reservation from stored reservationParams and
// build the /checkout confirmation URL. Used by the waiver flow (waiver.js / waiver_complete.js)
// so an unmonetized fleet that requires a waiver still creates-then-confirms instead of Stripe.
// Returns the relative confirmation URL (e.g. "checkout?...") on success; throws on failure.
export const createReservationAndBuildCheckoutUrl = async ({ params, operator_id }) => {
  const created = await createReservation({
    user_id: params.user_id,
    fleet_id: params.fleet_id,
    operator_id: operator_id || params.operator_id,
    start_date: params.start_date,
    end_date: params.end_date,
    // ACOS: forward either the vehicle-type OR the asset-type (+resolved offering id) so the
    // waiver flow can confirm an asset booking as well as a vehicle booking.
    vehicle_type_id: params.vehicle_type_id || null,
    asset_type_id: params.asset_type_id || null,
    reservation_id: params.reservation_id || null,
    min_increment_minutes: params.min_increment_minutes || null,
    note: params.note || null,
  });

  const url = 'checkout?operator=' + (operator_id || params.operator_id)
    + '&fleet=' + params.fleet_id
    + '&reservation_amount=0.00'
    + '&start_date=' + encodeURIComponent(created.start_date || params.start_date)
    + '&end_date=' + encodeURIComponent(created.end_date || params.end_date)
    + '&reservation_hash=' + encodeURIComponent(created.reservation_hash || '')
    + '&base_charge=0&taxes=0&daily_rate=0&coupon_percent=0'
    + '&note=' + encodeURIComponent(params.note || '')
    + '&quantity=1';
  return url;
};

export const checkVehicleAvailable = async (vehicle_type, fleet_id, start_date, end_date, min_increment_minutes = null) => {
  const query = {
    vehicle_type,
    fleet_id,
    start_date,
    end_date
  };
  // ACOS: chosen booking increment (60 = hourly). Omitted -> backend treats as daily.
  if (min_increment_minutes) {
    query.min_increment_minutes = min_increment_minutes;
  }
  const url = `${API_HOST_V3}/reservation-web/check-vehicle-available?` + new URLSearchParams(query);

  return fetch(url, {
    method: 'GET',
  }).then(async (response) => {
    if (response.ok) {
      return true;
    }
    // Surface the backend's specific reason (unavailable / outside operating hours / blackout).
    let message = 'There was a problem with your request. Please try again later';
    try {
      const errorData = await response.json();
      message = errorData.message || errorData.title || message;
    } catch { /* non-JSON error body */ }
    throw new Error(message);
  });
};

export const signUp = async (country_code, phone, code, operatorId) => {
  const url = `${API_HOST_V1}/signup`;
  const body = {
    password: 'password', 
    country_code,
    phone,
    code,
    operator_id: operatorId,
    account_type: 'user',
    fromWebApp: true
  };

  return fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/JSON',
    },
  })
  .then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error('Failed to sign in. Please try again.');
    }
  })
  .catch((error) => {
    console.error(error);
    throw error;
  });
};

export const getFleets = (operatorId) => {
  const url = `${API_HOST_V2}/reservation-web/fleets?`+ new URLSearchParams({
    operator_id: operatorId,
  });
  return fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error('There was a problem with your request. Please try again later');
    }
  }).then((data) => {
    // Filter to only include fleets with type 'reservation'
    if (data && data.fleets && Array.isArray(data.fleets)) {
      const reservationFleets = data.fleets.filter(fleet => fleet.type === 'reservation');
      return { ...data, fleets: reservationFleets };
    }
    return data;
  });
};


export const getPrice = (fleet_id, start_date, end_date, vehicle_type, coupon_id, addon_ids, qty = 1, min_increment_minutes = null, duration_mode = false) => {
  let params = {
    start_date,
    end_date,
    fleet_id,
    vehicle_type,
  };
  if (coupon_id) {
    params.coupon_id = coupon_id;
  }
  if (addon_ids && addon_ids.length > 0) {
    params.addon_ids = addon_ids.join(',');
  }
  if (qty && qty !== 1) {
    params.qty = qty;
  }
  // ACOS: chosen booking increment (60 = hourly). Omitted -> backend defaults to daily.
  if (min_increment_minutes) {
    params.min_increment_minutes = min_increment_minutes;
  }
  // ACOS: car-rental duration billing (daily = ceil(hours/24)).
  if (duration_mode) {
    params.duration_mode = 'true';
  }
  const url = `${API_HOST_V3}/reservation-web/reservation-price?` + new URLSearchParams(params);
  return fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error('There was a problem with your request. Please try again later');
    }
  });
};

export const getFleet = async (fleetId) => {
  const url = `${API_HOST_V2}/reservation-web/fleet?`+ new URLSearchParams({
    fleet_id: fleetId,
  });
  return fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error('There was a problem with your request. Please try again later');
    }
  })
};


export const getFleetVehicles = (fleetId) => {
  const url = `${API_HOST_V2}/reservation-web/vehicles?`;
  return fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error('There was a problem with your request. Please try again later');
    }
  });
};

export const getCoupon = (fleetId, code) => {
  const url = `${API_HOST_V3}/reservation-web/coupon?`+ new URLSearchParams({
    fleet_id: fleetId,
    code
  });
  return fetch(url, {
    method: 'GET',
  }).then((response) => {
    console.log("response:", response)
    if (response.ok) {
      return response;
    } else {
      throw new Error('There was a problem with your request. Please try again later');
    }
  });
};

export const getReservationSurcharges = (operatorId, fleet_fare_id) => {
  const url = `${API_HOST_V3}/reservation-web/surcharges?` + new URLSearchParams({
    operator_id: operatorId,
    fleet_fare_id: fleet_fare_id
  });
  return fetch(url, {
    method: 'GET',
  }).then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error('Failed to fetch reservation surcharges');
    }
  });
};

export const getAvailableVehicleTypes = async (fleet_id, start_date, end_date, min_increment_minutes = null) => {
  const params = {
    fleet_id,
    start_date,
    end_date
  };
  // ACOS: sub-day increment -> only list vehicle types with an active hourly offering.
  if (min_increment_minutes) {
    params.min_increment_minutes = min_increment_minutes;
  }
  const url = `${API_HOST_V3}/reservation-web/available-vehicle-types?` + new URLSearchParams(params);
  
  console.log('getAvailableVehicleTypes - URL:', url);
  console.log('getAvailableVehicleTypes - Params:', params);
  
  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  .then(async (response) => {
    console.log('getAvailableVehicleTypes - Response status:', response.status);
    console.log('getAvailableVehicleTypes - Response ok:', response.ok);
    
    if (response.ok) {
      const data = await response.json();
      console.log('getAvailableVehicleTypes - Success data:', data);
      return data;
    } else {
      // Surface the backend's specific reason (e.g. outside operating hours / blackout day).
      let message = 'Unable to check vehicle availability. Please try again.';
      try {
        const errorData = await response.json();
        message = errorData.message || errorData.title || message;
      } catch { /* non-JSON error body */ }
      console.error('getAvailableVehicleTypes - Error response:', message);
      throw new Error(message);
    }
  })
  .catch((error) => {
    console.error('getAvailableVehicleTypes - Catch error:', error);
    throw error;
  });
};

// ACOS: asset-offering availability for reservation-web. Analog of getAvailableVehicleTypes but
// for asset-based fleets (e.g. fleet 34 "BallyQ Reservations"). Unmonetized — offerings carry no
// price. Returns [{operator_reservation_id, asset_type_id, asset_type_name, min_increment_minutes,
// description, total_assets, reserved_count, available_count}].
export const getAvailableAssetTypes = async (fleet_id, start_date, end_date, min_increment_minutes = null) => {
  const params = {
    fleet_id,
    start_date,
    end_date
  };
  // ACOS: sub-day increment -> only list offerings with an active hourly window.
  if (min_increment_minutes) {
    params.min_increment_minutes = min_increment_minutes;
  }
  const url = `${API_HOST_V3}/reservation-web/available-asset-types?` + new URLSearchParams(params);

  console.log('getAvailableAssetTypes - URL:', url);
  console.log('getAvailableAssetTypes - Params:', params);

  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  .then(async (response) => {
    console.log('getAvailableAssetTypes - Response status:', response.status);
    if (response.ok) {
      const data = await response.json();
      console.log('getAvailableAssetTypes - Success data:', data);
      return data;
    } else {
      // Surface the backend's specific reason (e.g. outside operating hours / blackout day).
      let message = 'Unable to check asset availability. Please try again.';
      try {
        const errorData = await response.json();
        message = errorData.message || errorData.title || message;
      } catch { /* non-JSON error body */ }
      console.error('getAvailableAssetTypes - Error response:', message);
      throw new Error(message);
    }
  })
  .catch((error) => {
    console.error('getAvailableAssetTypes - Catch error:', error);
    throw error;
  });
};

export const getWaiverTemplate = async (operatorId, fleetId, userId) => {
  const params = {};
  
  // Add parameters that are provided
  if (operatorId) {
    params.operator_id = operatorId;
  }
  
  if (fleetId) {
    params.fleet_id = fleetId;
  }
  
  if (userId) {
    params.user_id = userId;
  }
    // Check if at least one parameter is provided
  if (Object.keys(params).length === 0) {
    return Promise.reject(new Error('No parameters provided, at least one of operatorId, fleetId, or userId is required'));
  }
  
  const url = `${API_HOST_V3}/reservation-web/waiver-template?` + new URLSearchParams(params);
    return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  .then((response) => {
    if (response.ok) {
      return response.json();
    } else {
      throw new Error('Failed to fetch waiver template');
    }
  })
  .catch((error) => {
    throw error;
  });
};

// Keeping the old function for backward compatibility but implementing using the new endpoint
export const getReservationAddons = (operatorId, fleet_fare_id) => {
  return getReservationSurcharges(operatorId, fleet_fare_id)
    .then(surcharges => {
      // Filter surcharges to only include those with surcharge_type='reservation_addon'
      // The response is already an array of surcharges
      const addons = Array.isArray(surcharges) ? 
        surcharges.filter(surcharge => surcharge.surcharge_type === 'reservation_addon') : 
        [];
      return { addons };
    });
};

// Supported image formats for waiver submission (photos and signatures)
const SUPPORTED_IMAGE_FORMATS = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'bmp', 'svg+xml'];

// Regex to validate base64 image data URI format
const BASE64_IMAGE_REGEX = /^data:image\/(jpeg|jpg|png|gif|webp|bmp|svg\+xml);base64,.+$/;

/**
 * Validates that an image value is in the correct base64 data URI format
 * @param {string} value - The image value to validate
 * @param {string} fieldName - Name of the field for error messages
 * @returns {{ valid: boolean, error: string | null }}
 */
export const validateImageFormat = (value, fieldName = 'Image') => {
  if (!value) {
    return { valid: false, error: `${fieldName} is required` };
  }

  // Check if it's a URL instead of base64
  if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('gs://')) {
    return { 
      valid: false, 
      error: `Please capture or upload an image directly for ${fieldName.toLowerCase()} instead of using a URL` 
    };
  }

  // Check if it has the required data URI prefix
  if (!value.startsWith('data:image/')) {
    return { 
      valid: false, 
      error: `Image format error for ${fieldName.toLowerCase()}. Please try capturing the image again` 
    };
  }

  // Validate the full format with regex
  if (!BASE64_IMAGE_REGEX.test(value)) {
    // Extract the format to provide a more specific error
    const formatMatch = value.match(/^data:image\/([^;]+);/);
    if (formatMatch) {
      const format = formatMatch[1];
      if (!SUPPORTED_IMAGE_FORMATS.includes(format)) {
        return { 
          valid: false, 
          error: `Unsupported image format "${format}" for ${fieldName.toLowerCase()}. Please use PNG, JPEG, or WebP` 
        };
      }
    }
    return { 
      valid: false, 
      error: `Invalid image format for ${fieldName.toLowerCase()}. Please try capturing the image again` 
    };
  }

  return { valid: true, error: null };
};

/**
 * Validates all waiver elements that contain images (photo and signature types)
 * @param {Array} elements - Array of waiver elements to validate
 * @returns {{ valid: boolean, errors: Array<{ elementId: string, error: string }> }}
 */
export const validateWaiverImages = (elements) => {
  const errors = [];

  for (const element of elements) {
    if (element.type === 'photo' || element.type === 'signature') {
      const fieldName = element.type === 'signature' ? 'Signature' : 'Photo';
      const validation = validateImageFormat(element.value, fieldName);
      
      if (!validation.valid) {
        errors.push({
          elementId: element.id,
          elementType: element.type,
          error: validation.error
        });
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

export const submitWaiver = async (userId, submitData) => {
  // Validate image formats before sending to server
  const imageValidation = validateWaiverImages(submitData);
  if (!imageValidation.valid) {
    // Return the first error for simplicity
    const firstError = imageValidation.errors[0];
    throw new Error(firstError.error);
  }

  const url = `${API_HOST_V3}/reservation-web/submit-waiver/${userId}`;
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(submitData),
  })
    .then(async (response) => {
      if (response.ok) {
        return response.json();
      } else {
        // Try to parse error response
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = null;
        }

        // Handle specific error cases
        if (response.status === 413) {
          throw new Error('Your photos are too large. Please try uploading smaller images or compress them before submitting.');
        } else if (response.status === 400) {
          const message = errorData?.error || errorData?.message || 'Missing user_id or invalid elements array';
          throw new Error(message);
        } else if (response.status === 500) {
          // Map server-side errors to user-friendly messages
          const serverError = errorData?.error || '';
          
          if (serverError.includes('Unsupported image format')) {
            throw new Error('Unsupported image format. Please use PNG, JPEG, or WebP for photos and signatures.');
          } else if (serverError.includes('must be base64 encoded') || serverError.includes('not a URL')) {
            throw new Error('Please capture or upload images directly instead of using URLs.');
          } else if (serverError.includes('Invalid photo data format') || serverError.includes('data URI prefix')) {
            throw new Error('Image format error. Please try capturing the photo or signature again.');
          }
          
          throw new Error('Server error occurred. Please try again later or contact support.');
        } else {
          // Generic error for other status codes
          const message = errorData?.error || errorData?.message || `Failed to submit waiver (Error ${response.status})`;
          throw new Error(message);
        }
      }
    })
    .catch((error) => {
      console.error('submitWaiver error:', error);
      throw error;
    });
};


